// El filtro por marca de las GENERACIONES —
// ADR-2026-09-13-las-generaciones-sin-marca-se-ven-en-todas.
//
// "De la marca X" significa `brand_id = X` **o sin marca**. Una generación sin `brand_id`
// (94k históricas: sin producto, o con un producto huérfano; y las pocas nuevas de
// empresas sin marcas o de n8n sin producto) no puede atribuirse a ninguna marca, y
// esconderla bajo TODAS hacía que el usuario pensara que se le habían borrado. Es el
// mismo criterio que la app ya aplica a los productos huérfanos (ADR-018:
// `brand_id.eq.X,brand_id.is.null` en `productsApi`): lo que no tiene marca es de la
// empresa, y la empresa se ve desde cualquiera de sus marcas.
//
// Vive en un helper y no en cada consulta porque son cuatro pantallas
// (`useCreacionesRecientes`, `GeneratedImagesHistory`, `GeneradasPickGrid` —marca y rubro—
// y `fetchHistorialImages`): escrito cinco veces, la primera que se olvide el
// `is.null` vuelve a esconder las mismas filas en esa pantalla sola.

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * El argumento de `.or()` de PostgREST para "de esta(s) marca(s) o sin marca".
 *
 * Valida que cada id sea un uuid: el valor se interpola en la URL de la consulta, y un
 * id que no lo sea o rompe la consulta (400 silencioso → grilla vacía) o, peor, filtra
 * otra cosa. Con una lista de una sola marca genera el mismo `eq` que con la marca
 * suelta, así el filtro tiene una única forma por caso.
 */
export function orDeMarca(marcas: string | string[]): string {
  const ids = Array.isArray(marcas) ? marcas : [marcas];
  if (ids.length === 0) throw new Error('orDeMarca: sin marcas no hay filtro');
  for (const id of ids) {
    if (!UUID_RE.test(id)) throw new Error(`orDeMarca: id de marca inválido: ${id}`);
  }
  const condicion = ids.length === 1 ? `brand_id.eq.${ids[0]}` : `brand_id.in.(${ids.join(',')})`;
  return `${condicion},brand_id.is.null`;
}

/**
 * Con qué marca se FILTRA una lista de creaciones de esta cuenta — `null` = sin filtrar.
 *
 * El criterio ya existía escrito a mano en dos lados (`marcas.length > 1 ? marcaActivaId
 * : null` en el shell, `cuentaMultiMarca ? currentBrandId : null` en `InterfazV2`) y
 * ahora lo necesitan también las dos superficies del Chat que se habían quedado sin
 * filtro (`show_recent_media` y `PickCreacionCard`), así que vive acá una sola vez.
 *
 * Las dos razones para no filtrar:
 *  - **Una sola marca** (ADR-2026-08-03-creaciones-recientes-por-marca): filtrar por la
 *    única marca de la cuenta no distingue nada, y sí esconde lo que esté atribuido a
 *    una marca que `brand_profiles` no lista (en borrador, o dada de baja).
 *  - **La marca todavía no resolvió**: `null` significa "no sé cuál", no "ninguna", y
 *    acotar con un id que no está es mostrar una grilla vacía sin motivo.
 */
export function marcaParaFiltrar(
  marcaActiva: string | null | undefined,
  variasMarcas: boolean,
): string | null {
  if (!variasMarcas) return null;
  return marcaActiva ?? null;
}
